import React, { useState, useMemo, useCallback } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  Box,
  Alert,
  Button,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { useInlineEdit } from '../../../hooks/useInlineEdit';
import DeleteConfirmDialog from '../../DeleteConfirmDialog';
import { EditableTextField } from '../../EditableCell';
import {
  getTableStyles,
  getRowStyles,
  getCellStyles,
  tableFeatures,
  initialTableState,
} from '../../../utils/mabScrapingTableConfig';

export default function ScraperTablePage({
  projects,
  rows,
  loading,
  error,
  setError,
  clearError,
  addItem,
  updateItem,
  deleteItem,
  editableFields,
  createColumnsFn,
  entityName,
  itemLabelField,
  selectLabelId,
  addButtonLabel,
  AddDialog,
  extraToolbarActions,
  onAddClick,
}) {
  const theme = useTheme();

  const [selectedProject, setSelectedProject] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  React.useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].scraper_name);
    }
  }, [projects, selectedProject]);

  const filteredRows = useMemo(() => {
    if (!selectedProject) return [];
    return rows.filter((row) => row.project_name === selectedProject);
  }, [rows, selectedProject]);

  const selectedProjectData = useMemo(
    () => projects.find((p) => p.scraper_name === selectedProject),
    [projects, selectedProject],
  );

  const {
    editingRowId,
    editedValues,
    saving,
    startEdit,
    cancelEdit,
    saveEdit,
    updateField,
  } = useInlineEdit({
    editableFields,
    onSave: updateItem,
    onError: setError,
  });

  const handleProjectChange = (_event, option) => {
    setSelectedProject(option?.scraper_name || '');
  };

  const handleAdd = useCallback(
    (...args) => addItem(...args, selectedProject),
    [addItem, selectedProject],
  );

  const handleDeleteClick = useCallback((row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete) return;
    try {
      setDeleting(true);
      await deleteItem(rowToDelete._id);
      handleDeleteClose();
    } catch (err) {
      console.error(`Error deleting ${entityName}:`, err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }, [rowToDelete, deleteItem, setError, handleDeleteClose, entityName]);

  const columns = useMemo(
    () =>
      createColumnsFn({
        editingRowId,
        saving,
        onStartEdit: startEdit,
        onCancelEdit: cancelEdit,
        onSaveEdit: saveEdit,
        onDeleteClick: handleDeleteClick,
      }),
    [editingRowId, saving, startEdit, cancelEdit, saveEdit, handleDeleteClick, createColumnsFn],
  );

  const tableStyles = useMemo(() => getTableStyles(theme), [theme]);

  const table = useMaterialReactTable({
    columns,
    data: filteredRows,
    getRowId: (row) => row._id,
    initialState: initialTableState,
    state: { isLoading: loading },
    ...tableFeatures,
    ...tableStyles,
    muiTableBodyCellProps: ({ row, column }) => {
      const isEditing = editingRowId === row.original._id;

      let children;
      if (isEditing && editableFields.includes(column.id)) {
        children = (
          <EditableTextField
            value={editedValues[column.id] || ''}
            onChange={(value) => updateField(column.id, value)}
            autoFocus={column.id === editableFields[0]}
          />
        );
      }

      return {
        sx: getCellStyles(theme),
        children,
      };
    },
    muiTableBodyRowProps: ({ row }) =>
      getRowStyles(theme, editingRowId === row.original._id),
  });

  if (error && !rows.length) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading {entityName}s: {error}
      </Alert>
    );
  }

  const hasProjects = projects.length > 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {hasProjects && (
          <Autocomplete
            size="small"
            sx={{ minWidth: 300 }}
            options={projects}
            value={selectedProjectData || null}
            onChange={handleProjectChange}
            disableClearable
            getOptionLabel={(option) => option.site_name || option.scraper_name}
            isOptionEqualToValue={(opt, val) => opt.scraper_name === val.scraper_name}
            renderInput={(params) => (
              <TextField {...params} id={selectLabelId} label="Select Project" />
            )}
          />
        )}

        <Typography variant="body2" color="text.secondary">
          {filteredRows.length} {entityName}{filteredRows.length !== 1 ? 's' : ''} in this project
        </Typography>

        {extraToolbarActions && (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            {extraToolbarActions}
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onAddClick ? onAddClick(selectedProject) : setAddDialogOpen(true)}
          disabled={!selectedProject}
          sx={{
            ...(!extraToolbarActions && { ml: 'auto' }),
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#5f94de',
            boxShadow: theme.shadows[2],
            '&:hover': { boxShadow: theme.shadows[4] },
          }}
        >
          {addButtonLabel}
        </Button>

        {selectedProjectData?.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ width: '100%', mt: 0.5, fontStyle: 'italic' }}
          >
            {selectedProjectData.description}
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
      >
        <MaterialReactTable table={table} />
      </Paper>

      {!onAddClick && (
        <AddDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onSave={handleAdd}
          projectName={selectedProject}
        />
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        itemLabel={rowToDelete?.[itemLabelField]}
        itemType={entityName}
        saving={deleting}
      />
    </Box>
  );
}
